import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { BulkUpdateUnreleasedMaterials, UpdateUnreleasedMaterial } from '@repo/backend/crt'
import { PRODUCT_CATEGORIES, type ProductCategory } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { useMaterialProductTypes } from './useMaterials'

export const unreleasedMaterialsSearchSchema = z.object({
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  brandCode: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  priceSegment: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export type UnreleasedMaterialsSearch = z.infer<typeof unreleasedMaterialsSearchSchema>

async function getUnreleasedMaterials(search: UnreleasedMaterialsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['unreleased-materials'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )
  return await res.json()
}

export type UnreleasedMaterialsList = Awaited<ReturnType<typeof getUnreleasedMaterials>>
export type UnreleasedMaterialRow = UnreleasedMaterialsList['results'][number]

/** The filtered list as the import file format, so a user can edit it in Excel and import it back. */
async function getUnreleasedMaterialsCsv(search: UnreleasedMaterialsSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()
  return fetchCsvExport(`/crt/unreleased-materials/export?${queryParams}`, signal)
}

export const unreleasedMaterialsExportQueryOptions = (queries: UnreleasedMaterialsSearch) => ({
  queryKey: ['unreleasedMaterialsExport', queries] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getUnreleasedMaterialsCsv(queries, signal),
})

async function getIncompleteUnreleasedMaterialsCount(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['unreleased-materials']['incomplete-count'].$get(
    {},
    { init: { signal } },
  )
  return await res.json()
}

export const incompleteUnreleasedMaterialsCountQueryOptions = {
  queryKey: ['unreleasedMaterials', 'incomplete-count'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getIncompleteUnreleasedMaterialsCount(signal),
}

export function useIncompleteUnreleasedMaterialsCount() {
  return useQuery(incompleteUnreleasedMaterialsCountQueryOptions)
}

export type MaterialGroupProductCategories = Record<string, ProductCategory | null>

async function getMaterialGroups(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['material-groups'].$get({}, { init: { signal } })
  const materialGroups = await res.json()

  return {
    options: materialGroups.map((mg) => ({ value: mg.code, label: mg.name })),
    // Keyed by code so the edit dialogs can flag a product type set on an FMC group.
    productCategories: Object.fromEntries(
      materialGroups.map((mg) => [mg.code, mg.productCategory]),
    ) as MaterialGroupProductCategories,
  }
}

async function getBrands(signal?: AbortSignal) {
  const api = getApi()

  const [systemBrands, overrideBrands] = await Promise.all([
    api.crt.brands
      .$get({ query: { isOrderable: 'true' } }, { init: { signal } })
      .then((res) => res.json()),
    api.crt['unreleased-materials'].brands.$get({}, { init: { signal } }).then((res) => res.json()),
  ])

  const allBrands = Array.from(new Set([...systemBrands, ...overrideBrands])).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  )

  return allBrands.map((brand) => ({ value: brand, label: brand }))
}

async function getVariants(signal?: AbortSignal) {
  const api = getApi()

  const [systemVariants, overrideVariants] = await Promise.all([
    api.crt.variants.options
      .$get({ query: { isOrderable: 'true' } }, { init: { signal } })
      .then((res) => res.json()),
    api.crt['unreleased-materials'].variants
      .$get({}, { init: { signal } })
      .then((res) => res.json()),
  ])

  const allVariants = Array.from(new Set([...systemVariants, ...overrideVariants])).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  )

  return allVariants.map((variant) => ({ value: variant, label: variant }))
}

export function useUnreleasedMaterials(queries: UnreleasedMaterialsSearch) {
  const results = useQueries({
    queries: [
      {
        queryKey: ['unreleasedMaterials', queries],
        queryFn: ({ signal }: { signal: AbortSignal }) => getUnreleasedMaterials(queries, signal),
      },
      {
        queryKey: ['unreleasedMaterials', 'materialGroups'],
        queryFn: ({ signal }: { signal: AbortSignal }) => getMaterialGroups(signal),
      },
      {
        queryKey: ['unreleasedMaterials', 'brands'],
        queryFn: ({ signal }: { signal: AbortSignal }) => getBrands(signal),
      },
      {
        queryKey: ['unreleasedMaterials', 'variants'],
        queryFn: ({ signal }: { signal: AbortSignal }) => getVariants(signal),
      },
    ],
  })

  const [mainQuery, materialGroupsQuery, brandsQuery, variantsQuery] = results

  const productTypesQuery = useMaterialProductTypes()
  const productTypeOptions = (productTypesQuery.data ?? []).map((pt) => ({ value: pt, label: pt }))

  return {
    data: mainQuery.data,
    isLoading: mainQuery.isLoading,
    isPending: mainQuery.isPending,
    isError: mainQuery.isError,
    error: mainQuery.error,
    materialGroupProductCategories: materialGroupsQuery.data?.productCategories ?? {},
    mergedOptions: {
      materialGroups: materialGroupsQuery.data?.options ?? [],
      brands: brandsQuery.data ?? [],
      variants: variantsQuery.data ?? [],
      productTypes: productTypeOptions,
    },
    mergedOptionsLoading: {
      materialGroups: materialGroupsQuery.isLoading,
      brands: brandsQuery.isLoading,
      variants: variantsQuery.isLoading,
      productTypes: productTypesQuery.isLoading,
    },
  }
}

async function updateUnreleasedMaterial(id: number, data: UpdateUnreleasedMaterial) {
  const api = getApi()
  const res = await api.crt['unreleased-materials'][':id'].$patch({
    param: { id: String(id) },
    json: data,
  })
  return await res.json()
}

async function bulkUpdateUnreleasedMaterials(updates: BulkUpdateUnreleasedMaterials['updates']) {
  const api = getApi()
  const res = await api.crt['unreleased-materials'].bulk.$patch({
    json: { updates },
  })
  return await res.json()
}

export function useUpdateUnreleasedMaterial() {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateUnreleasedMaterial'],
    mutationFn: ({ id, data }: { id: number; data: UpdateUnreleasedMaterial }) =>
      updateUnreleasedMaterial(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unreleasedMaterials'] })
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('unreleasedMaterials.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.unreleasedMaterials.updateFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useBulkUpdateUnreleasedMaterials() {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['bulkUpdateUnreleasedMaterials'],
    mutationFn: bulkUpdateUnreleasedMaterials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unreleasedMaterials'] })
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('unreleasedMaterials.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.unreleasedMaterials.updateFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
